/**
 * Parse a human-typed duration into minutes.
 *
 * Accepts the shapes a timekeeper actually types: `3h 20m`, `3h20m`, `2h`,
 * `45m`, `3.5h`, `1 hour 30 minutes`, `3:20`, and a bare `90`.
 *
 * A bare number means MINUTES. That is what the manual time-entry field has
 * always meant, so reinterpreting it would silently rewrite entries people
 * type from muscle memory.
 *
 * Returns null when the input is not a duration at all, so callers can refuse
 * to submit rather than guess. Zero parses to 0 — a real value that callers
 * reject on their own terms.
 */

const COLON = /^(\d+):([0-5]\d)$/;
const UNITS = /^(?:(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours))?\s*(?:(\d+(?:\.\d+)?)\s*(?:m|min|mins|minute|minutes))?$/;
const BARE = /^\d+(?:\.\d+)?$/;

/** Strip the float noise in e.g. 0.1 * 60 === 6.000000000000001. */
function tidy(minutes: number): number {
  return Math.round(minutes * 1e6) / 1e6;
}

export function parseDurationToMinutes(input: string): number | null {
  const text = input.trim().toLowerCase();
  if (!text) return null;

  const colon = COLON.exec(text);
  if (colon) {
    return Number(colon[1]) * 60 + Number(colon[2]);
  }

  const units = UNITS.exec(text);
  if (units && (units[1] !== undefined || units[2] !== undefined)) {
    const hours = units[1] === undefined ? 0 : Number(units[1]);
    const minutes = units[2] === undefined ? 0 : Number(units[2]);
    return tidy(hours * 60 + minutes);
  }

  if (BARE.test(text)) {
    return tidy(Number(text));
  }

  return null;
}
